// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { useGasv3Listener } from '@atlassian/post-office-context';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';
import { defineMessages, useIntl } from 'react-intl-next';
import { useEffect } from 'react';
import { useFireEngagementPlatformAnalyticsEvents } from '@post-office/analytics';

import { createRender } from './types';
import spotlightImage from '../../../assets/spotlight-image.png';

/**
 * ⚠️  CRITICAL SAFETY WARNING FOR POST OFFICE MESSAGES ⚠️
 *
 * Messages in Post Office can go live and reach customers very easily, particularly
 * with implicit message creation. Before merging this code, ensure you have:
 *
 * 1. ✅ Set up a Statsig filter to control message visibility
 *    📖 https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/pre-defined-filters/#statsig-filter
 *
 * 2. ✅ Set up an environment filter to prevent accidental production deployment
 *    📖 https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/pre-defined-filters/#environment-filter
 *
 * 3. ✅ If using implicit messages, configure them properly in implicit-messages
 *    📖 https://developer.atlassian.com/platform/post-office/message-template/implicit-messages/#what-are-implicit-messages
 *
 * 4. ✅ Verify that you're not rendering anything you dont want customers to see.
 *    📖 https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/213556
 *
 * Remember: Test thoroughly in staging environments before enabling in production!
 */

export const formattedMessages = defineMessages({
	spotlightHeading: {
		id: 'INAPP-2270.spotlight.title',
		defaultMessage: 'Create docs without leaving Jira',
		description: 'Title for the spotlight',
	},
	spotlightBody: {
		id: 'INAPP-2270.spotlight.body',
		defaultMessage: 'Collaborate on requirements, meetings notes, retros, and more.',
		description: 'Body for the spotlight',
	},
	spotlightPrimary: {
		id: 'INAPP-2270.spotlight.primary',
		defaultMessage: 'Ok',
		description: 'Primary button for the spotlight',
	},
	spotlightSecondary: {
		id: 'INAPP-2270.spotlight.secondary',
		defaultMessage: 'Dismiss',
		description: 'Secondary button for the spotlight',
	},
});

export const render = createRender(
	({ onDismissed, messageInstanceId, userId, tenantId, locale }) => {
		const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();
		const { fireAnalyticsEvent } = useFireEngagementPlatformAnalyticsEvents();
		const showMessage = useGasv3Listener({
			meta: {
				teamName: 'endeavour',
			},
			action: 'ready',
			context: {
				source: 'navigationNextScreen',
				attributes: {
					previousSubProduct: 'software',
				},
			},
			actionSubject: 'sidebar',
			analyticsType: 'UI',
		});
		const { formatMessage } = useIntl();

		const handleDismissClick = (dismiss?: boolean) => {
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'ok',
					clickedElement: 'button',
					buttonType: 'primary',
				},
				action: { actionType: dismiss ? 'dismiss' : 'react' },
				additionalMetadataFromFrontend: {},
			});
			onDismissed(messageInstanceId);
		};

		useEffect(() => {
			if (showMessage) {
				void messageInstanceViewed();
				if (userId && tenantId) {
					fireAnalyticsEvent({
						messageType: 'inapp',
						messageId: 'INAPP-2270',
						userId,
						tenantId,
						locale,
					});
				}
			}
		}, [fireAnalyticsEvent, tenantId, userId, locale, showMessage, messageInstanceViewed]);

		return (
			<ManualMessageViewedEvent>
				{showMessage ? (
					<SpotlightTransition>
						<Spotlight
							image={spotlightImage}
							heading={formatMessage(formattedMessages.spotlightHeading)}
							actions={[
								{
									onClick: () => handleDismissClick(),
									text: formatMessage(formattedMessages.spotlightPrimary),
									appearance: 'primary',
								},
								{
									onClick: () => handleDismissClick(true),
									text: formatMessage(formattedMessages.spotlightSecondary),
									appearance: 'primary',
								},
							]}
							target="spotlight-sidebar-project-pages"
							key="spotlight-sidebar-project-pages"
							targetBgColor="#FFF"
							targetRadius={3}
							dialogWidth={275}
							dialogPlacement="right top"
						>
							{formatMessage(formattedMessages.spotlightBody)}
						</Spotlight>
					</SpotlightTransition>
				) : null}
			</ManualMessageViewedEvent>
		);
	},
);

export default render;
